// Lara AI · Pipeline Workflows
// Scaffolding page — one to-do card per canonical Talmia stage listing
// (a) what AI is meant to do at that stage and (b) when a human takes over.
// Sourced from `crm_stages` so labels stay in sync with the pipeline.
//
// Also renders "Pipedrive source pipelines" reference cards at the top so
// Ops can see how each source pipeline routes into Talmia 2 (Lead / Deal /
// Need Tier).
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Workflow, Bot, Sparkles, ChevronRight, Clock, AlertTriangle,
  Loader2, GitBranch,
} from "lucide-react";
import Layout from "../../components/Layout";
import PipedriveSourceCard from "./PipedriveSourceCard";
import PipelineWorkflowsPanel from "./PipelineWorkflowsPanel";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STAGE_TONE = {
  slate:   "text-slate-300 border-slate-500/40 bg-slate-500/[0.05]",
  fuchsia: "text-fuchsia-300 border-fuchsia-500/40 bg-fuchsia-500/[0.05]",
  sky:     "text-sky-300 border-sky-500/40 bg-sky-500/[0.05]",
  purple:  "text-purple-300 border-purple-500/40 bg-purple-500/[0.05]",
  orange:  "text-orange-300 border-orange-500/40 bg-orange-500/[0.05]",
  amber:   "text-amber-300 border-amber-500/40 bg-amber-500/[0.05]",
  yellow:  "text-yellow-200 border-yellow-500/40 bg-yellow-500/[0.05]",
  emerald: "text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.05]",
  rose:    "text-rose-300 border-rose-500/40 bg-rose-500/[0.05]",
  cyan:    "text-cyan-300 border-cyan-500/40 bg-cyan-500/[0.05]",
};
const tone = (c) => STAGE_TONE[c] || STAGE_TONE.slate;

function StageCard({ stage, idx }) {
  return (
    <div
      className={`rounded-xl border p-4 flex flex-col gap-3 ${tone(stage.color)}`}
      data-testid={`pipeline-workflow-${stage.key}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-6 h-6 shrink-0 rounded-md border grid place-items-center font-mono text-[11px] font-semibold ${tone(stage.color)}`}>
            {idx + 1}
          </span>
          <div className="min-w-0">
            <div className="text-white font-semibold text-sm truncate">{stage.label}</div>
            <div className="text-[9.5px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
              {stage.key}
            </div>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9.5px] font-mono uppercase tracking-widest border bg-slate-500/15 text-slate-400 border-slate-500/30 shrink-0">
          TBD
        </span>
      </div>

      {/* AI role */}
      <div className="space-y-1">
        <div className="text-[9.5px] font-mono uppercase tracking-widest text-fuchsia-300 flex items-center gap-1">
          <Bot className="w-2.5 h-2.5" /> AI role
        </div>
        <p className="text-[12px] text-slate-300 leading-snug">
          {stage.ai_role || "—"}
        </p>
      </div>

      {/* Human trigger */}
      <div className="space-y-1">
        <div className="text-[9.5px] font-mono uppercase tracking-widest text-orange-300 flex items-center gap-1">
          <Sparkles className="w-2.5 h-2.5" /> Human steps in
        </div>
        <p className="text-[12px] text-slate-300 leading-snug">
          {stage.human_trigger || "—"}
        </p>
      </div>

      {/* Meta */}
      <div className="mt-auto pt-2 border-t border-white/[0.06] flex items-center flex-wrap gap-x-3 gap-y-1 text-[10.5px] font-mono uppercase tracking-widest text-slate-500">
        {stage.probability > 0 && (
          <span>Win · {stage.probability}%</span>
        )}
        {stage.rot_days && (
          <span className="text-amber-400 inline-flex items-center gap-1">
            <AlertTriangle className="w-2.5 h-2.5" /> {stage.rot_days}d rot
          </span>
        )}
        {!stage.rot_days && (
          <span className="inline-flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> No rot</span>
        )}
      </div>

      <div className="text-[9.5px] font-mono uppercase tracking-widest text-slate-600 pt-1">
        Wiring TBD → Workflow Builder
      </div>
    </div>
  );
}

export default function PipelineWorkflows() {
  const [stages, setStages] = useState([]);
  const [pipedrivePipelines, setPipedrivePipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/crm/stages`,          { withCredentials: true }),
      axios.get(`${API}/pipedrive/pipelines`, { withCredentials: true }),
    ])
      .then(([sRes, pRes]) => {
        setStages(sRes.data.items || []);
        setPipedrivePipelines(pRes.data.pipelines || []);
      })
      .catch((e) => setError(e.response?.data?.detail || "Failed to load stages"))
      .finally(() => setLoading(false));
  }, []);

  // Non-tiered source pipelines get top billing — they're what Ops needs
  // to keep an eye on. Tiered pipelines (BLACK/PLATINUM/…) are collapsed
  // under a details/summary block.
  const nonTiered = pipedrivePipelines.filter((p) => p.route !== "deal");
  const tiered    = pipedrivePipelines.filter((p) => p.route === "deal");

  return (
    <Layout>
      <div className="space-y-6" data-testid="lara-pipeline-workflows">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-purple-400 font-mono mb-1 flex items-center gap-1.5">
              <Workflow className="w-3 h-3" /> Lara AI · Pipeline Workflows
            </div>
            <h1 className="text-3xl font-display font-bold tracking-tight flex items-center gap-3">
              Pipeline Workflows
              <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/[0.04] text-slate-400 border border-white/10">
                Scaffold
              </span>
            </h1>
            <p className="text-slate-400 text-sm mt-1 max-w-2xl">
              Pipedrive source pipelines routed into Talmia 2, plus to-do
              cards for each canonical Deal Pipeline stage — what AI is
              <em> meant</em> to do and where a human takes the wheel.
            </p>
          </div>
          <Link
            to="/deals"
            data-testid="workflows-open-pipeline"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/10 bg-white/[0.03] text-sm text-slate-300 hover:text-white hover:bg-white/[0.06]"
          >
            <GitBranch className="w-3.5 h-3.5" /> Open Deal Pipeline <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        {error && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 text-rose-200 text-sm px-3 py-2" data-testid="workflows-error">
            {error}
          </div>
        )}

        {/* Live matrix + PD SYNC button — new headline surface. */}
        <PipelineWorkflowsPanel />

        {/* Pipedrive source pipelines — top billing for the two non-tiered
            pipelines that need routing attention. */}
        {!loading && nonTiered.length > 0 && (
          <section className="space-y-3" data-testid="workflows-pipedrive-sources">
            <div className="flex items-center gap-2">
              <div className="text-[10px] uppercase tracking-widest text-orange-400 font-mono flex items-center gap-1.5">
                <GitBranch className="w-3 h-3" /> Pipedrive · Source pipelines
              </div>
              <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 border border-amber-500/40">
                Non-tiered · Needs routing
              </span>
            </div>
            <p className="text-slate-400 text-sm max-w-3xl">
              These Pipedrive pipelines are <strong>not tier-aligned</strong> —
              they&rsquo;re the entry lanes for new leads and new paying customers.
              Talmia 2 routes them automatically to the pipeline shown on each
              card.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {nonTiered.map((p) => <PipedriveSourceCard key={p.id} pipeline={p} />)}
            </div>
          </section>
        )}

        {/* Collapsed reference for the tiered Pipedrive pipelines. */}
        {!loading && tiered.length > 0 && (
          <details className="group rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3" data-testid="workflows-pipedrive-tiered">
            <summary className="flex items-center gap-2 cursor-pointer text-[10.5px] font-mono uppercase tracking-widest text-slate-400 hover:text-white select-none">
              <ChevronRight className="w-3 h-3 group-open:rotate-90 transition-transform" />
              Tiered Pipedrive pipelines · {tiered.length}
              <span className="text-slate-600">(BLACK · PLATINUM · GOLD · SILVER · BRONZE …)</span>
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
              {tiered.map((p) => <PipedriveSourceCard key={p.id} pipeline={p} />)}
            </div>
          </details>
        )}

        {/* Divider before Talmia canonical stage cards */}
        {!loading && (
          <div className="flex items-center gap-3 pt-2">
            <div className="text-[10px] uppercase tracking-widest text-purple-400 font-mono flex items-center gap-1.5">
              <Workflow className="w-3 h-3" /> Talmia · Canonical stage automations
            </div>
            <div className="flex-1 h-px bg-white/[0.06]" />
          </div>
        )}

        {loading ? (
          <div className="p-12 text-center text-slate-500 flex items-center justify-center gap-2" data-testid="workflows-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading stages…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4" data-testid="workflows-grid">
            {stages.map((s, idx) => <StageCard key={s.key} stage={s} idx={idx} />)}
          </div>
        )}

        <div className="text-[10.5px] text-slate-600 font-mono flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" />
          Stage definitions live in <code className="text-slate-500">crm_pipeline_config.py</code> —
          edit label inline on the Deal Pipeline (admin) or refine
          <code className="text-slate-500 ml-1">ai_role</code> /
          <code className="text-slate-500">human_trigger</code> in code.
        </div>
      </div>
    </Layout>
  );
}
